'use client';

import { useEffect, useState } from 'react';
import { ArrowRight } from '../Icons/Icons';

interface VideoTransitionScreenProps {
  nextLessonTitle: string;
  onCountdownComplete: () => void;
  onSkip: () => void;
}

export default function VideoTransitionScreen({
  nextLessonTitle,
  onCountdownComplete,
  onSkip,
}: VideoTransitionScreenProps) {
  const [countdown, setCountdown] = useState(3);
  const totalTime = 3;
  const circumference = 3 * Math.PI * 28; // 2πr where r=28

  useEffect(() => {
    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onCountdownComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onCountdownComplete]);

  return (
    <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-5 bg-[var(--Surfaces-Overlay-Sheen-75,rgba(0,0,0,0.75))] p-2 backdrop-blur-lg sm:gap-6 sm:p-3 md:gap-8 md:p-4">
      <div className="flex w-[90%] max-w-[844px] flex-col items-center gap-3 px-3 sm:gap-4 sm:px-4 md:px-5">
        <h4 className="cs-h4 text-center text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]">
          Up next
        </h4>

        <div className="flex w-full flex-col items-center gap-3 sm:gap-4">
          <h2 className="cs-h2 text-center text-[var(--Text-Default-Strong)] px-2">
            {nextLessonTitle}
          </h2>
          <p className="cs-caption text-center text-[var(--Text-Default-Light)">
            The lesson will start in ...
          </p>
        </div>

        <div className="video-transition-timer relative aspect-square w-11 shrink-0 sm:w-14 md:w-16">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Background circle */}
            <circle
              cx="48"
              cy="48"
              r="28"
              stroke="var(--Color-Grayscale-925-Bold, #212121)"
              strokeWidth="8"
              fill="none"
            />
            {/* Animated progress circle with gradient and glow */}
            <g filter="url(#filter0_d_37583_7827)">
              <circle
                cx="48"
                cy="48"
                r="28"
                stroke="url(#paint0_linear_37583_7827)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (countdown / totalTime)}
                style={{
                  transition: 'stroke-dashoffset 1s linear'
                }}
              />
            </g>
            <defs>
              <filter id="filter0_d_37583_7827" x="0" y="0" width="96" height="96" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset />
                <feGaussianBlur stdDeviation="8" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.676 0 0 0 0 0.46 0 0 0 0 1 0 0 0 0.65 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_37583_7827" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_37583_7827" result="shape" />
              </filter>
              <linearGradient id="paint0_linear_37583_7827" x1="16" y1="16" x2="80" y2="80" gradientUnits="userSpaceOnUse">
                <stop style={{ stopColor: 'var(--Color-Extended-Amethyst-Amethyst-Accent, #AC75FF)' }} />
                <stop offset="1" style={{ stopColor: 'var(--Color-Extended-Amethyst-Amethyst-Shade, #654A8C)' }} />
              </linearGradient>
            </defs>
          </svg>
          <span className="absolute inset-0 grid place-items-center text-[10px] font-semibold text-[var(--Color-Grayscale-White,#FFF)] sm:text-xs md:text-sm">
            {countdown}s
          </span>
        </div>

        <button
          onClick={onSkip}
          className="cs-button text-[var(--Text-Default-Light)] !cs-caption"
        >
          <div className="flex items-center gap-2 cursor-pointer">
            Keep learning
            <ArrowRight className="opacity-60" width={20} height={20} />
          </div>
        </button>
      </div>
    </div>
  );
}
